// src/components/modifiers/ModifierGroup.tsx
import React from 'react';
import { ModifierGroup as ModifierGroupType } from '../../types/modifier';
import { ModifierOption } from './ModifierOption';

interface ModifierGroupProps {
  group: ModifierGroupType;
  isOptionSelected: (optionId: string) => boolean;
  getOptionQuantity: (optionId: string) => number;
  onSelectOption: (optionId: string) => void;
  onQuantityChange: (optionId: string, quantity: number) => void;
}

export const ModifierGroup: React.FC<ModifierGroupProps> = ({
  group,
  isOptionSelected,
  getOptionQuantity,
  onSelectOption,
  onQuantityChange,
}) => {
  // Calculate selected count for this group
  const getSelectedCount = () => {
    return group.options.reduce((count, option) => {
      const quantity = getOptionQuantity(option.id);
      return count + quantity;
    }, 0);
  };

  const selectedCount = getSelectedCount();

  // Determine helper text based on allowMultiples and maxAllowed
  const getHelperText = () => {
    if (!group.allowMultiples) {
      return 'Select one';
    }
    
    if (group.maxAllowed > 0) {
      const remaining = group.maxAllowed - selectedCount;
      
      if (remaining === 0) {
        return `${selectedCount} selected (max)`;
      }
      return `Select up to ${group.maxAllowed} (${remaining} remaining)`;
    }
    
    return null;
  };

  // Check if max selections reached (for checkbox groups)
  const isMaxReached = group.allowMultiples && group.maxAllowed > 0 && selectedCount >= group.maxAllowed;

  return (
    <div className="mb-6">
      {/* Group Header - Match reference UI */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold text-gray-900 uppercase tracking-wide">
          {group.name}
          {group.isRequired && (
            <span className="text-red-500 ml-1">*</span>
          )}
        </h3>
        {getHelperText() && (
          <span className="text-sm text-gray-500">
            {getHelperText()}
          </span>
        )}
      </div>

      {/* Options List */}
      <div className="space-y-2">
        {group.options.map((option) => {
          const isSelected = isOptionSelected(option.id);
          const isDisabled = !option.isEnabled || (isMaxReached && !isSelected);
          
          return (
            <ModifierOption
              key={option.id}
              option={option}
              isSelected={isSelected}
              quantity={getOptionQuantity(option.id)}
              allowMultiples={group.allowMultiples}
              onSelect={() => onSelectOption(option.id)}
              onQuantityChange={(qty) => onQuantityChange(option.id, qty)}
              disabled={isDisabled}
            />
          );
        })}
      </div>

      {/* Validation Error - Removed, only show asterisk for required */}
    </div>
  );
};
